import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Loader2, Star, X } from 'lucide-react';

import { useAuth } from '@/modules/auth/hooks/use-auth';
import { queryClient } from '@/shared/lib/query-client';
import { enhancedToast } from '@/shared/utils/enhanced-toast';
import { getImageUrl } from '@/shared/utils/image';
import { cn } from '@/shared/lib/utils';

import { getPendingRating, rateOrderWithSource } from '../rating/api';
import {
  dismissRatingBanner,
  isRatingBannerDismissed,
  trackRatingPromptShown,
} from '../rating/storage';
import { RATING_MAX, RATING_MIN } from '../types/rate-order';

interface PendingRatingBannerProps {
  /** Na página da loja: só o pedido desta loja. */
  merchantSlug?: string;
  className?: string;
}

/**
 * PRAT-111 — faixa "Avalie seu último pedido" na home e na página da loja: o
 * último pedido concluído, sem nota e dentro dos 14 dias (a API decide). A nota
 * sai com 1 toque na estrela; o comentário fica na tela do pedido. Some ao
 * avaliar ou no "X" (lembrado por pedido neste aparelho).
 */
export function PendingRatingBanner({ merchantSlug, className }: PendingRatingBannerProps) {
  const { isAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  const [dismissedId, setDismissedId] = useState<string | null>(null);
  const [ratedValue, setRatedValue] = useState(0);

  const query = useQuery({
    queryKey: ['pending-rating', merchantSlug ?? null],
    queryFn: () => getPendingRating(merchantSlug),
    // Sem login a API responde 401 e o cliente HTTP mandaria para o login.
    enabled: isAuthenticated,
    staleTime: 60_000,
    retry: false,
  });

  const order = query.data ?? null;
  const visible =
    !!order &&
    order.id !== dismissedId &&
    !isRatingBannerDismissed(order.id) &&
    new Date(order.ratable_until).getTime() > Date.now();

  useEffect(() => {
    if (visible && order) trackRatingPromptShown(order.id, 'banner', order.merchant.slug);
  }, [visible, order]);

  const rateMutation = useMutation({
    mutationFn: (rating: number) => rateOrderWithSource(order!.id, rating, 'banner'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', order!.id] });
    },
    onError: () => {
      setRatedValue(0);
      queryClient.invalidateQueries({ queryKey: ['pending-rating'] });
      enhancedToast.error('Não foi possível enviar a avaliação', {
        description: 'Tente novamente em instantes.',
      });
    },
  });

  if (!visible || !order) return null;

  const store = order.merchant.name || 'a loja';
  const number = order.display_id ? ` #${order.display_id}` : '';
  const done = rateMutation.isSuccess;

  const handleDismiss = () => {
    dismissRatingBanner(order.id);
    setDismissedId(order.id);
    if (done) queryClient.invalidateQueries({ queryKey: ['pending-rating'] });
  };

  const handleRate = (value: number) => {
    if (rateMutation.isPending || done) return;
    setRatedValue(value);
    rateMutation.mutate(value);
  };

  const stars = Array.from({ length: RATING_MAX - RATING_MIN + 1 }, (_, i) => RATING_MIN + i);

  return (
    <div
      className={cn('mt-3 rounded-2xl border border-gray-200 bg-white p-4', className)}
      data-testid="pending-rating-banner"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {order.merchant.image && (
            <img
              src={getImageUrl(order.merchant.image)}
              alt=""
              onError={e => (e.currentTarget.style.display = 'none')}
              className="h-10 w-10 shrink-0 rounded-xl object-cover"
            />
          )}
          <div className="min-w-0">
            <p className="text-[15px] font-bold text-gray-900">
              {done ? 'Obrigado pela nota!' : 'Avalie seu último pedido'}
            </p>
            <p className="truncate text-[13px] text-gray-600">
              {done ? `Sua avaliação ajuda ${store}.` : `${store} · Pedido${number}`}
            </p>
          </div>
        </div>
        <button
          type="button"
          aria-label="Dispensar"
          className="-m-2 p-2 text-gray-400"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Nota do pedido">
          {stars.map(star => (
            <button
              key={star}
              type="button"
              role="radio"
              aria-checked={ratedValue === star}
              aria-label={`${star} ${star === 1 ? 'estrela' : 'estrelas'}`}
              disabled={rateMutation.isPending || done}
              onClick={() => handleRate(star)}
              className="clickable-small disabled:cursor-default"
            >
              <Star
                className={cn(
                  'h-8 w-8 transition-colors',
                  star <= ratedValue ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                )}
              />
            </button>
          ))}
        </div>
        {rateMutation.isPending && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}
        {done && (
          <button
            type="button"
            className="text-[13px] font-bold text-primary"
            onClick={() => navigate(`/orders/${order.id}?comentar=1`)}
          >
            Comentar
          </button>
        )}
      </div>
    </div>
  );
}
